import { Lock } from 'lucide-preact';
import { formatRelativeTime } from '@pinmerge/shared';
import type { LocalComment } from '../storage';
import type { Active } from '../lib/types';
import { viewportAnchor } from '../lib/pin-geometry';
import { isOwnComment } from '../lib/comments';
import { AvatarSlot, DEFAULT_AVATAR_BG } from './AvatarSlot';
import { t, tPlural } from '../i18n';

const MARKER_SIZE = 28;

export interface MarkerLayerProps {
  comments: LocalComment[];
  active: Active;
  ownToken: string;
  scroll: { x: number; y: number };
  hoveredId: string | null;
  setHoveredId: (id: string | null) => void;
  failedAvatars: Set<string>;
  onAvatarFailed: (slug: string) => void;
  apiUrl: string;
  loggedIn: boolean;
  canPickWithoutLogin: boolean;
  onMarkerClick: (id: string) => void;
}

export function MarkerLayer({
  comments,
  active,
  ownToken,
  scroll,
  hoveredId,
  setHoveredId,
  failedAvatars,
  onAvatarFailed,
  apiUrl,
  loggedIn,
  canPickWithoutLogin,
  onMarkerClick,
}: MarkerLayerProps) {
  return (
    <>
      {/* Persistent comment markers — top-level only; replies don't get pins.
          The marker button itself is the avatar circle: AvatarSlot fills it
          and the parent border-radius clips the SVG into a pin shape. */}
      {comments.filter((c) => !c.parentId).map((c) => {
        const anchor = viewportAnchor(c, scroll);
        const isActiveMarker = active?.kind === 'view' && active.id === c.id;
        const mine = isOwnComment(c, ownToken);
        const bg = c.avatar?.bg ?? DEFAULT_AVATAR_BG;
        return (
          <button
            key={c.id}
            type="button"
            class={`marker ${mine ? '' : 'marker--other'} ${isActiveMarker ? 'marker--active' : ''}`}
            style={`left: ${anchor.vx - MARKER_SIZE / 2}px; top: ${anchor.vy - MARKER_SIZE / 2}px; background: ${bg};`}
            onClick={() => onMarkerClick(c.id)}
            onMouseEnter={() => setHoveredId(c.id)}
            onMouseLeave={() => setHoveredId(null)}
            aria-label={t('marker.aria-label', { name: c.authorName })}
          >
            <AvatarSlot
              apiUrl={apiUrl}
              avatarId={c.avatar?.id ?? null}
              avatarBg={c.avatar?.bg ?? null}
              authorName={c.authorName}
              isOther={!mine}
              extraClass="pin__avatar--marker"
              failedAvatars={failedAvatars}
              onAvatarFailed={onAvatarFailed}
            />
          </button>
        );
      })}

      {/* Pin hover tooltip — author + relative time + first line of comment +
          reply count. Suppressed when the same comment's popup is already open
          (the popup carries the same info and more). */}
      {hoveredId &&
        !(active?.kind === 'view' && active.id === hoveredId) &&
        (() => {
          const c = comments.find((x) => x.id === hoveredId);
          if (!c || c.parentId) return null;
          const anchor = viewportAnchor(c, scroll);
          const replyCount = comments.filter((r) => r.parentId === c.id).length;
          const firstLine = c.text.split('\n')[0] ?? '';
          const TRUNC = 80;
          const preview = firstLine.length > TRUNC ? firstLine.slice(0, TRUNC) + '…' : firstLine;
          const mine = isOwnComment(c, ownToken);
          // Anchor below the marker (marker center + half-marker + small gap).
          const tipLeft = Math.max(8, anchor.vx);
          const tipTop = anchor.vy + MARKER_SIZE / 2 + 4;
          return (
            <div class="pin-tooltip" style={`left: ${tipLeft}px; top: ${tipTop}px;`}>
              <div class="pin-tooltip__head">
                <AvatarSlot
                  apiUrl={apiUrl}
                  avatarId={c.avatar?.id ?? null}
                  avatarBg={c.avatar?.bg ?? null}
                  authorName={c.authorName}
                  isOther={!mine}
                  extraClass="pin__avatar--xs"
                  failedAvatars={failedAvatars}
                  onAvatarFailed={onAvatarFailed}
                />
                <span class="pin-tooltip__name">{c.authorName}</span>
                <span class="pin-tooltip__time">{formatRelativeTime(c.createdAt)}</span>
              </div>
              {canPickWithoutLogin ? (
                <div class="pin-tooltip__text">{preview}</div>
              ) : (
                <div class="pin-tooltip__text pin-tooltip__text--locked">
                  <Lock size={11} aria-hidden="true" /> {t('marker.locked-content')}
                </div>
              )}
              {loggedIn && replyCount > 0 && (
                <div class="pin-tooltip__replies">
                  {tPlural('marker.reply-count-one', 'marker.reply-count-other', replyCount)}
                </div>
              )}
            </div>
          );
        })()}
    </>
  );
}
